<template>
  <div class="tab-container">
    <app-spinner v-if="!page_loaded" />
    <template v-else>
      <template v-if="(table.items.length && supplierData.verified) || !supplierData.verified">
        <app-cells>
          <h2 class="title">Bank accounts</h2>
          <app-button v-if="!supplierData.verified" @click.native="onShowModal(null)" size="small">Add</app-button>
        </app-cells>
        <v-client-table
          :data="table.items"
          :columns="table.columns"
          :options="table.options"
          class="table-default"
        >
          <template #h__bank><div class="table-default__left">Bank</div></template>
          <template #h__account_number><div class="table-default__left">Number</div></template>
          <template #h__currency><div class="table-default__left">Currency</div></template>
          <template #h__account_name><div class="table-default__left">Account name</div></template>
          <template #bank="{ row }">
            <div class="table-default__left">
              {{ row.bank.short_name }} ({{ row.bank.swift }})
            </div>
          </template>
          <template #account_number="{ row }">
            <div class="table-default__left">{{ row.account_number }} ({{ row.currency?.symbol }})</div>
          </template>
          <template #account_name="{ row }"><div class="table-default__left">{{ row.account_name || '-' }}</div></template>
          <template #controls="{ row }">
            <div v-if="!supplierData.verified" class="table-default__controls">
              <button @click="onShowModal(row)" type="button">
                <img src="@/assets/img/edit-table-icon.svg" alt="Edit">
              </button>
              <button @click="onShowDeleteModal(row.id, 1)" type="button">
                <img src="@/assets/img/delete-table-icon.svg" alt="Delete">
              </button>
            </div>
          </template>
        </v-client-table>
      </template>
      <template v-if="(table_mobile.items.length && supplierData.verified) || !supplierData.verified">
        <app-cells>
          <h2 class="title">Mobile accounts</h2>
          <app-button v-if="!supplierData.verified" @click.native="onShowModalMobile(null)" size="small">Add</app-button>
        </app-cells>
        <v-client-table
          :data="table_mobile.items"
          :columns="table_mobile.columns"
          :options="table_mobile.options"
          class="table-default"
        >
          <template #h__provider><div class="table-default__left">Provider</div></template>
          <template #h__account_name><div class="table-default__left">Account name</div></template>
          <template #h__account_number><div class="table-default__left">Number</div></template>
          <template #provider="{ row }"><div class="table-default__left">{{ row.provider.name }}</div>
          </template>
          <template #account_number="{ row }"><div class="table-default__left">{{ row.account_number }}</div></template>
          <template #account_name="{ row }"><div class="table-default__left">{{ row.account_name }}</div></template>
          <template #controls="{ row }">
            <div v-if="!supplierData.verified" class="table-default__controls">
              <button @click="onShowModalMobile(row)" type="button">
                <img src="@/assets/img/edit-table-icon.svg" alt="Edit">
              </button>
              <button @click="onShowDeleteModal(row.id, 2)" type="button">
                <img src="@/assets/img/delete-table-icon.svg" alt="Delete">
              </button>
            </div>
          </template>
        </v-client-table>
      </template>
      <app-cells>
        <h2 class="title">Payment methods</h2>
        <app-button v-if="!supplierData.verified" @click.native="onShowModalPaymentMethods(null)" size="small">Add</app-button>
      </app-cells>
      <v-client-table
        :data="table_payment_methods.items"
        :columns="table_payment_methods.columns"
        :options="table_payment_methods.options"
        class="table-default table-default--indent"
      >
        <template #account="{ row }">
          <template v-if="row.payment_method.code === 'bank'">
            <template v-if="row.bank_account">{{ row.bank_account.account_number }}<br>{{ row.bank_account.bank.short_name }}</template>
            <template v-else>-</template>
          </template>
          <template v-else-if="row.payment_method.code === 'mobile'">
            <template v-if="row.mobile_account">{{ row.mobile_account.account_number }}<br>{{ row.mobile_account.provider.name }}</template>
            <template v-else>-</template>
          </template>
          <template v-else>-</template>
        </template>
        <template #active="{ row }">
          <span
            :style="{ backgroundColor: row.is_active ? '#77b017' : '#e85c20' }"
            class="status-tag"
          >
            {{ row.is_active ? 'Active' : 'Not active' }}
          </span>
        </template>
        <template #controls="{ row }">
          <div v-if="!supplierData.verified" class="table-default__controls">
            <button @click="onShowModalPaymentMethods(row)" type="button">
              <img src="@/assets/img/edit-table-icon.svg" alt="Edit">
            </button>
          </div>
        </template>
      </v-client-table>
      <div class="downloads" v-if="is_general_manager || is_accountant_supervisor">
        <div class="downloads__item" :class="{'downloads__item--loading': paymentVerifiedLoader}">
          <div class="downloads__left">
            <div class="downloads__info">
              <span class="downloads__name downloads__name--no-events">Verified</span>
            </div>
          </div>
          <div class="downloads__right">
            <div class="downloads__values">
              <span class="downloads__key">Status:</span>
              <app-switcher
                :readonly="is_accountant_supervisor && supplierData.verified"
                :value="supplierData.verified"
                @change="$emit('changeVerify', $event)"
              />
            </div>
            <template v-if="supplierData.verified && !paymentVerifiedLoader">
              <div class="downloads__values">
                <span class="downloads__key">Verified at:</span>
                <span class="downloads__value downloads__width">{{ dateTimeFormat(supplierData.verified_at) }}</span>
              </div>
              <div class="downloads__values">
                <span class="downloads__key">Verified by:</span>
                <div class="downloads__person downloads__width">
                  <div class="downloads__photo">
                    <img :src="getUserAvatar(supplierData.verified_by?.crop.crop_url)" alt="Person">
                  </div>
                  <span class="downloads__value">{{ supplierData.verified_by?.full_name || '-' }}</span>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </template>
    <modal
      name="create_bank"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('create_bank')" class="popup__close" type="button"></button>
        <div class="popup__title">
          {{ is_popup_edit ? 'Edit' : 'Add' }} bank account
        </div>
        <div class="popup__body">
          <form @submit.prevent="onCheckBankForm">
            <app-form-group label="Bank" required>
              <app-select
                v-model="form.bank"
                :options="bank_options"
                :clearable="false"
                label="short_name"
                placeholder="Bank"
                :error="$v.form.bank.$error"
              />
              <template #error>
                <div v-if="$v.form.bank.$dirty && !$v.form.bank.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Currency" required>
              <app-select
                v-model="form.currency"
                :options="currency_options"
                :clearable="false"
                label="code"
                placeholder="Currency"
                :error="$v.form.currency.$error"
              />
              <template #error>
                <div v-if="$v.form.currency.$dirty && !$v.form.currency.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Number" label-for="account_number" required>
              <app-input
                v-model="form.account_number"
                id="account_number"
                placeholder="Number"
                name="account_number"
                :error="$v.form.account_number.$error"
              />
              <template #error>
                <div v-if="$v.form.account_number.$dirty && !$v.form.account_number.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Account name" label-for="account_name">
              <app-input
                v-model="form.account_name"
                id="account_name"
                placeholder="Account name"
                name="account_name"
              />
            </app-form-group>
            <app-cells :indent="false" position="center">
              <app-button :disabled="$v.form.$error" type="submit" ref="submit_bank">
                {{ is_popup_edit ? 'Save' : 'Add' }}
              </app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal
      name="create_mobile"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('create_mobile')" class="popup__close" type="button"></button>
        <div class="popup__title">
          {{ is_popup_edit ? 'Edit' : 'Add' }} mobile account
        </div>
        <div class="popup__body">
          <form @submit.prevent="onCheckMobileForm">
            <app-form-group label="Provider" required>
              <app-select
                v-model="form_mobile.provider"
                :options="provider_options"
                :clearable="false"
                placeholder="Provider"
                :error="$v.form_mobile.provider.$error"
              />
              <template #error>
                <div v-if="$v.form_mobile.provider.$dirty && !$v.form_mobile.provider.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Number" label-for="mobile_number" required>
              <app-input
                v-model="form_mobile.account_number"
                id="mobile_number"
                placeholder="Number"
                name="mobile_number"
                :error="$v.form_mobile.account_number.$error"
              />
              <template #error>
                <div v-if="$v.form_mobile.account_number.$dirty && !$v.form_mobile.account_number.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Account name" label-for="mobile_name" required>
              <app-input
                v-model="form_mobile.account_name"
                id="mobile_name"
                placeholder="Account name"
                name="mobile_name"
                :error="$v.form_mobile.account_name.$error"
              />
              <template #error>
                <div v-if="$v.form_mobile.account_name.$dirty && !$v.form_mobile.account_name.required">Required field</div>
              </template>
            </app-form-group>
            <app-cells :indent="false" position="center">
              <app-button :disabled="$v.form_mobile.$error" type="submit" ref="submit_mobile">
                {{ is_popup_edit ? 'Save' : 'Add' }}
              </app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal
      name="create_payment_method"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('create_payment_method')" class="popup__close" type="button"></button>
        <div class="popup__title">Payment methods</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckPMForm">
            <app-form-group label="Priority" label-for="priority" required>
              <app-input
                v-model.number="form_pm.priority"
                id="priority"
                placeholder="Priority"
                name="priority"
                type="number"
                :error="$v.form_pm.priority.$error"
              />
              <template #error>
                <div v-if="$v.form_pm.priority.$dirty && !$v.form_pm.priority.required">Required field</div>
              </template>
            </app-form-group>
            <template v-if="!pm_to_action">
              <app-form-group label="Method" class="form-group--mh" required>
                <app-cells position="start" :indent="false">
                  <app-radio
                    @change.native="onChangePaymentMethod"
                    v-model="form_pm.payment_method"
                    v-for="item of payment_method_options"
                    :key="item.code"
                    :value="item.code"
                    :checked="form_pm.payment_method"
                    name="payment_method"
                  >
                    <template #label>{{ item.name }}</template>
                  </app-radio>
                </app-cells>
              </app-form-group>
              <app-form-group v-if="form_pm.payment_method === 'bank'" label="Bank account" required>
                <app-select
                  v-model="form_pm.account"
                  :options="bank_account_options"
                  :clearable="false"
                  :searchable="false"
                  :display-field="option => `${option.account_number} (${option.currency?.symbol}) - ${option.bank?.short_name}`"
                  label="account_number"
                  placeholder="Bank account"
                  :error="$v.form_pm.account.$error"
                />
                <template #error>
                  <div v-if="$v.form_pm.account.$dirty && !$v.form_pm.account.required">Required field</div>
                </template>
              </app-form-group>
              <app-form-group v-if="form_pm.payment_method === 'mobile'" label="Mobile account" required>
                <app-select
                  v-model="form_pm.account"
                  :options="mobile_account_options"
                  :clearable="false"
                  :searchable="false"
                  :display-field="option => `${option.account_number} (${option.provider?.name})`"
                  label="account_number"
                  placeholder="Mobile account"
                  :error="$v.form_pm.account.$error"
                />
                <template #error>
                  <div v-if="$v.form_pm.account.$dirty && !$v.form_pm.account.required">Required field</div>
                </template>
              </app-form-group>
            </template>
            <app-form-group v-else>
              <app-checkbox v-model="form_pm.is_active" label="Active" />
            </app-form-group>
            <app-cells :indent="false" position="center">
              <app-button :disabled="$v.form_pm.$error" type="submit" ref="submit_pm">
                {{ pm_to_action ? 'Save' : 'Add' }}
              </app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal
      name="delete-modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('delete-modal')" class="popup__close" type="button"></button>
        <div class="popup__title popup__title--flex">
          <img src="@/assets/img/warning-round-icon.svg" alt="">
          Delete account
        </div>
        <div class="popup__body">
          <div class="popup__subt">Are you sure you want to delete this account?</div>
          <app-cells position="center" :indent="false">
            <app-button @click.native="$modal.hide('delete-modal')" theme="dark">Cancel</app-button>
            <app-button v-if="type_to_delete === 1" @click.native="onDeleteBankAccount" ref="delete_account">Yes</app-button>
            <app-button v-else @click.native="onDeleteMobileAccount" ref="delete_account">Yes</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import {
  deleteAdministrativeSupplierBankAccount,
  deleteAdministrativeSupplierMobileAccount,
  getAdministrativeSupplierBankAccounts,
  getAdministrativeSupplierBankAccountSearch,
  getAdministrativeSupplierMobileAccounts,
  getAdministrativeSupplierMobileAccountSearch,
  getAdministrativeSuppliersPaymentMethods,
  getCurrencyList,
  getFinanceBanks,
  getFinanceMobileMoneyProviders,
  getFinancePaymentMethods,
  patchAdministrativeSupplierBankAccount,
  patchAdministrativeSupplierMobileAccount,
  patchAdministrativeSuppliersPaymentMethods,
  postAdministrativeSupplierBankAccount,
  postAdministrativeSupplierMobileAccount,
  postAdministrativeSuppliersPaymentMethods,
} from '@/http'
import { required, requiredIf } from 'vuelidate/lib/validators'
import { format, parseISO } from 'date-fns'

export default {
  name: 'SuppliersDetailAccounts',
  props: {
    supplierData: {
      required: true,
      type: Object,
    },
    paymentVerifiedLoader: {
      required: true,
      type: Boolean,
    }
  },
  data() {
    return {
      page_loaded: false,
      form: {},
      form_mobile: {},
      form_pm: {},
      bank_options: [],
      currency_options: [],
      provider_options: [],
      mobile_account_options: [],
      bank_account_options: [],
      payment_method_options: [],
      is_popup_edit: false,
      account_to_delete: null,
      type_to_delete: null,
      pm_to_action: null,
      table: {
        items: [],
        columns: ['bank', 'account_number', 'account_name', 'controls'],
        options: {
          headings: {
            bank: 'Bank',
            account_number: 'Number',
            account_name: 'Account name',
            controls: '',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 50,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      table_mobile: {
        items: [],
        columns: ['provider', 'account_number', 'account_name', 'controls'],
        options: {
          headings: {
            provider: 'Provider',
            account_number: 'Number',
            account_name: 'Account name',
            controls: '',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 50,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      table_payment_methods: {
        items: [],
        columns: ['priority', 'payment_method.name', 'account', 'active', 'controls'],
        options: {
          headings: {
            'priority': 'Priority',
            'payment_method.name': 'Method',
            'account': 'Account',
            'active': 'Active',
            'controls': '',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 50,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
    }
  },
  validations: {
    form: {
      bank: { required },
      currency: { required },
      account_number: { required },
    },
    form_mobile: {
      provider: { required },
      account_number: { required },
      account_name: { required },
    },
    form_pm: {
      priority: { required },
      payment_method: {
        required: requiredIf(function() {
          return this.payment_method_options.length > 1 && !this.pm_to_action
        })
      },
      account: {
        required: requiredIf(function() {
          return ['bank', 'mobile'].includes(this.form_pm.payment_method) && !this.pm_to_action
        })
      },
    }
  },
  computed: {
    is_general_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('general_manager')
    },
    is_accountant_supervisor() {
      return this.$store.state.userGroups.map(item => item.code).includes('accountant_supervisor')
    },
  },
  created() {
    Promise.all([this.fetchItemsBank(), this.fetchItemsMobile(), this.fetchItemsPM()])
      .then(() => {
        this.page_loaded = true
      })
    getFinanceBanks().then(res => { this.bank_options = res.data })
    getCurrencyList().then(res => { this.currency_options = res.data })
    getFinanceMobileMoneyProviders().then(res => { this.provider_options = res.data })
    getFinancePaymentMethods().then(res => { this.payment_method_options = res.data })
  },
  methods: {
    async fetchItemsBank() {
      const response = await getAdministrativeSupplierBankAccounts(Number(this.$route.params.id))
      this.table.items = response.data
    },
    async fetchItemsMobile() {
      const response = await getAdministrativeSupplierMobileAccounts(Number(this.$route.params.id))
      this.table_mobile.items = response.data
    },
    async fetchItemsPM() {
      const response = await getAdministrativeSuppliersPaymentMethods(Number(this.$route.params.id))
      this.table_payment_methods.items = response.data
    },
    onCheckBankForm() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendBankForm()
      }
    },
    sendBankForm() {
      this.$refs.submit_bank.preload = true
      if (!this.is_popup_edit) {
        postAdministrativeSupplierBankAccount(Number(this.$route.params.id), this.normalizeForm())
          .finally(() => { this.$refs.submit_bank.preload = false })
          .then(() => {
            this.fetchItemsBank()
            this.$notify({
              type: 'success',
              title: 'Account added',
            })
            this.$modal.hide('create_bank')
          })
      } else {
        patchAdministrativeSupplierBankAccount(Number(this.$route.params.id), this.form.id, this.normalizeForm())
          .finally(() => { this.$refs.submit_bank.preload = false })
          .then(() => {
            this.fetchItemsBank()
            this.$notify({
              type: 'success',
              title: 'Account saved',
            })
            this.$modal.hide('create_bank')
          })
      }
    },
    normalizeForm() {
      const normalizedForm = { ...this.form }

      normalizedForm.bank = normalizedForm.bank.id
      normalizedForm.currency = normalizedForm.currency.code

      return normalizedForm
    },
    onCheckMobileForm() {
      this.$v.form_mobile.$touch()
      if (this.$v.form_mobile.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendMobileForm()
      }
    },
    sendMobileForm() {
      this.$refs.submit_mobile.preload = true
      if (!this.is_popup_edit) {
        postAdministrativeSupplierMobileAccount(Number(this.$route.params.id), {...this.form_mobile, provider: this.form_mobile.provider.id})
          .finally(() => { this.$refs.submit_mobile.preload = false })
          .then(() => {
            this.fetchItemsMobile()
            this.$notify({
              type: 'success',
              title: 'Account added',
            })
            this.$modal.hide('create_mobile')
          })
      } else {
        patchAdministrativeSupplierMobileAccount(Number(this.$route.params.id), this.form_mobile.id, {...this.form_mobile, provider: this.form_mobile.provider.id})
          .finally(() => { this.$refs.submit_mobile.preload = false })
          .then(() => {
            this.fetchItemsMobile()
            this.$notify({
              type: 'success',
              title: 'Account saved',
            })
            this.$modal.hide('create_mobile')
          })
      }
    },
    onCheckPMForm() {
      this.$v.form_pm.$touch()
      if (this.$v.form_pm.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendPMForm()
      }
    },
    sendPMForm() {
      this.$refs.submit_pm.preload = true
      if (!this.pm_to_action) {
        const data = {...this.form_pm}
        if (data.account) data.account = data.account.id
        else delete data.account
        postAdministrativeSuppliersPaymentMethods(Number(this.$route.params.id), data)
          .finally(() => { this.$refs.submit_pm.preload = false })
          .then(() => {
            this.fetchItemsPM()
            this.$notify({
              type: 'success',
              title: 'Payment method added',
            })
            this.$modal.hide('create_payment_method')
          })
      } else {
        patchAdministrativeSuppliersPaymentMethods(Number(this.$route.params.id), this.pm_to_action, this.form_pm)
          .finally(() => { this.$refs.submit_pm.preload = false })
          .then(() => {
            this.fetchItemsPM()
            this.$notify({
              type: 'success',
              title: 'Payment method saved',
            })
            this.$modal.hide('create_payment_method')
          })
      }
    },
    onChangePaymentMethod() {
      this.form_pm.account = null
      if (this.form_pm.payment_method === 'bank') {
        getAdministrativeSupplierBankAccountSearch(Number(this.$route.params.id))
          .then(response => { this.bank_account_options = response.data })
      }
      if (this.form_pm.payment_method === 'mobile') {
        getAdministrativeSupplierMobileAccountSearch(Number(this.$route.params.id))
          .then(response => { this.mobile_account_options = response.data })
      }
    },
    onShowModal(row) {
      if (row) {
        this.is_popup_edit = true
        this.form = { ...row }
      } else {
        this.is_popup_edit = false
        this.form = {}
      }
      this.$v.form.$reset()
      this.$modal.show('create_bank')
    },
    onShowModalMobile(row) {
      if (row) {
        this.is_popup_edit = true
        this.form_mobile = {
          id: row.id,
          provider: row.provider,
          account_number: row.account_number,
          account_name: row.account_name,
        }
      } else {
        this.is_popup_edit = false
        this.form_mobile = {}
      }
      this.$v.form_mobile.$reset()
      this.$modal.show('create_mobile')
    },
    onShowModalPaymentMethods(row) {
      this.pm_to_action = row?.id
      if (row) {
        this.form_pm = { priority: row.priority, is_active: row.is_active }
      } else {
        this.form_pm = {
          account: null,
          payment_method: this.payment_method_options.length === 1 ? this.payment_method_options[0].code : null
        }
      }
      this.$v.form_pm.$reset()
      this.$modal.show('create_payment_method')
    },
    onShowDeleteModal(id, type) {
      this.account_to_delete = id
      this.type_to_delete = type
      this.$modal.show('delete-modal')
    },
    onDeleteBankAccount() {
      this.$refs.delete_account.preload = true
      deleteAdministrativeSupplierBankAccount(Number(this.$route.params.id), this.account_to_delete)
        .finally(() => { this.$refs.delete_account.preload = false })
        .then(() => {
          this.fetchItemsBank()
          this.$notify({
            type: 'success',
            title: 'Account deleted',
          })
          this.$modal.hide('delete-modal')
        })
    },
    onDeleteMobileAccount() {
      this.$refs.delete_account.preload = true
      deleteAdministrativeSupplierMobileAccount(Number(this.$route.params.id), this.account_to_delete)
        .finally(() => { this.$refs.delete_account.preload = false })
        .then(() => {
          this.fetchItemsMobile()
          this.$notify({
            type: 'success',
            title: 'Account deleted',
          })
          this.$modal.hide('delete-modal')
        })
    },
    getUserAvatar(crop_url) {
      if (crop_url) return crop_url
      return require('@/assets/img/temp/avatar-slug2.jpg')
    },
    dateTimeFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy HH:mm') : '-'
    },
  }
}
</script>
